import React from "react";
import "./Card.css";
import { getAddress, getDayName, getEventDate, getEventHours, getPlaceHoursString, getPlaceHoursByDay, checkIsWholeTimeOpen, getTodayHours } from "./placeUtils";
import { Link } from "react-router-dom";
import ImagePlaceholder from "./ImagePlaceholder";
import { useLanguage } from "./LanguageContext";

const DAYS_OF_WEEK = [
  { dayKey: "Pon", value: 1 },
  { dayKey: "Wt", value: 2 },
  { dayKey: "Śr", value: 3 },
  { dayKey: "Czw", value: 4 },
  { dayKey: "Pt", value: 5 },
  { dayKey: "Sob", value: 6 },
  { dayKey: "Ndz", value: 0 },
];

function Card({ cardsCategory, cardsSize, place, event }) {
  const { t } = useLanguage();
  const isEvent = cardsCategory === "events";
  const item = isEvent ? event : place;

  if (!item) {
    return null;
  }

  const description = item.description ?? "";
  const profilePath = isEvent
    ? `/${item.category.toLowerCase()}/${item.subcategory.toLowerCase()}/profil_event/${item._id}`
    : `/${item.category.toLowerCase()}/${item.subcategory.toLowerCase()}/profil/${item._id}`;
  const eventDate = item.startDate
    ? `${getEventDate(item)} ${getDayName(item.startDate.split("-")[2], parseInt(item.startDate.split("-")[1]) - 1, item.startDate.split("-")[0])}`
    : "";

  const currentDayIndex = new Date().getDay();

  const getHoursStatus = () => {
    const tags = item?.tags?.map((tag) => tag.name) ?? [];
    if (tags.includes("kościoły") || tags.includes("teatry") || tags.includes("kina")) {
      return "open";
    }

    const dayInfo = getTodayHours(item);
    if (!dayInfo || !dayInfo.open || dayInfo.open === "") {
      return "notOpenToday";
    }

    if (checkIsWholeTimeOpen(item, new Date().getDay())) {
      return "open";
    }

    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const [openHour, openMinute] = dayInfo.open.split(":").map(Number);
    const [closeHour, closeMinute] = dayInfo.close.split(":").map(Number);
    const openMinutes = openHour * 60 + openMinute;
    const closeMinutes = closeHour * 60 + closeMinute;

    if (nowMinutes >= openMinutes && nowMinutes < closeMinutes) {
      return nowMinutes >= closeMinutes - 60 ? "closingSoon" : "open";
    }

    return "closed";
  };

  const statusClass = getHoursStatus();

  const renderHoursText = (val) => {
    const dayInfo = getPlaceHoursByDay(item, val, t);
    if (!dayInfo || dayInfo.open === "") {
      return t("closed");
    }
    if (checkIsWholeTimeOpen(item, val)) {
      return t("allDay");
    }
    if (dayInfo.open === t("churchHours") || dayInfo.open === t("scheduleHours")) {
      return dayInfo.open;
    }
    return `${dayInfo.open} - ${dayInfo.close}`;
  };

  return (
    <article className={`listing-card ${cardsSize ? "listing-card--large" : "listing-card--compact"} ${isEvent ? "listing-card--event" : "listing-card--place"}`}>
      <div className="listing-card-media">
        <Link to={profilePath} className="listing-card-media-link" aria-label={t(isEvent ? "viewEvent" : "viewPlace")}>
          <ImagePlaceholder className="listing-card-image" src={item.images?.[0]?.img} type={isEvent ? "event" : "place"} alt={item.title} />
          <span className="listing-card-city">Kraków</span>
          {isEvent && eventDate ? <span className="listing-card-date">{eventDate}</span> : null}
        </Link>
      </div>

      <div className="listing-card-content">
        <div>
          <h2 className="listing-card-title"><Link to={profilePath}>{item.title}</Link></h2>
          <p className="listing-card-description">{description.length > 200 ? `${description.substring(0, 200)}...` : description}</p>
        </div>

        <div className="listing-card-meta">
          <div>
            <span>{t("cardAddress")}</span>
            <strong>{getAddress(item)}</strong>
          </div>

          {isEvent ? (
            <>
              {eventDate ? <div><span>{t("cardDate")}</span><strong>{eventDate}</strong></div> : null}
              {item.startDate ? <div><span>{t("cardTime")}</span><strong>{getEventHours(item)}</strong></div> : null}
            </>
          ) : (
            <div
              className="listing-card-hours-container"
            >
              <span>{t("cardOpenToday")}</span>
              <strong
                className={`listing-card-hours-trigger hours-status-${statusClass}`}
              >
                <span className="listing-card-hours-text">{statusClass === "notOpenToday" ? t("cardNotOpenToday") : statusClass === "closed" ? t("cardClosed") : getPlaceHoursString(item, t)}</span>
                <span className="listing-card-hours-icon" aria-hidden="true">▾</span>
              </strong>

              <div className="listing-card-hours-tooltip" role="tooltip">
                <div className="listing-card-hours-tooltip-title">{t("openingHours")}</div>
                <ul className="listing-card-hours-list">
                  {DAYS_OF_WEEK.map((d) => {
                    const dayHours = getPlaceHoursByDay(item, d.value, t);
                    const isClosed = !dayHours || dayHours.open === "";
                    const isToday = d.value === currentDayIndex;

                    return (
                      <li
                        key={d.value}
                        className={`listing-card-hours-row ${isToday ? "is-today" : ""} ${isClosed ? "is-closed" : "is-open"}`}
                      >
                        <span className="listing-card-hours-day">{d.dayKey}:</span>
                        <span className="listing-card-hours-time">{renderHoursText(d.value)}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
        </div>

        <Link to={profilePath} className="listing-card-action">
          {t(isEvent ? "viewEvent" : "viewPlace")}
        </Link>
      </div>
    </article>
  );
}

export default Card;